import * as React from 'react';

import { NewbuildingCarousel, TDirection, TOnSlideChangeParams } from '@cian/frontend-newbuilding-carousel-component';
import { isDeepEqual } from '@cian/newbuilding-utils';
import { Image } from '@cian/ui-kit/image';
import { ELayoutTypes, ILayout } from '../../types/newbuilding/layouts';
import * as style from './LayoutsCarousel.css';
import { ArrowControls } from './parts/ArrowControls';
import { LayoutTypeSwitcher } from './parts/LayoutTypeSwitcher';
import { SlideInfo } from './parts/SlideInfo';
import { Tour } from './parts/Tour';
import { firstPaintOptions, mediaSettings } from './utils/carouselSettings';

export interface ILayoutsCarouselProps {
  media: ILayout[];
  selectedLayout: number;
  selectLayout(index: number): void;
  onButtonClick(): void;
  on3DTourTypeChange(): void;
}

export interface ILayoutsCarouselState {
  slides: React.ReactNode[];
  currentSlide: number;
  layoutType: ELayoutTypes;
}

export class LayoutsCarousel extends React.Component<ILayoutsCarouselProps, ILayoutsCarouselState> {
  public constructor(props: ILayoutsCarouselProps) {
    super(props);

    const { media, selectedLayout } = this.props;

    this.state = {
      layoutType: ELayoutTypes.Layout,
      slides: media && this.prepareSlides(media),
      currentSlide: selectedLayout,
    };
  }

  public componentDidMount() {
    const { media } = this.props;
    this.setState({ slides: media && this.prepareSlides(media) });
  }

  public componentDidUpdate(prevProps: ILayoutsCarouselProps) {
    const { media, selectedLayout } = this.props;

    if (prevProps.selectedLayout !== selectedLayout) {
      this.setState({
        currentSlide: selectedLayout <= media.length ? selectedLayout : 1,
      });
    }

    if (prevProps.media.length !== media.length || !isDeepEqual(prevProps.media, media)) {
      this.setState({
        slides: this.prepareSlides(media),
        currentSlide: 1,
        layoutType: ELayoutTypes.Layout,
      });
    }
  }

  public prepareSlides = (layoutData: ILayout[], layoutType = ELayoutTypes.Layout): React.ReactNode[] => {
    const isIE = process.env.IS_BROWSER_BUILD && /MSIE|Trident/.test(window.navigator.userAgent);
    const currentIndex = (this.state ? this.state.currentSlide : 1) - 1;

    const slides = layoutData.map((layout, i) => (
      <div className={style['container']} key={layout.image.fullUrl}>
        <div className={style['layout']}>
          {layoutType === ELayoutTypes.Layout && (
            <div className={style['image']}>
              <Image src={layout.image.fullUrl} objectFit={'contain'} />
            </div>
          )}
          {layoutType === ELayoutTypes.Tour && layout.tourUrl && currentIndex === i && (
            <div className={style['tour']}>
              <Tour tourUrl={layout.tourUrl} isIE={!!isIE} />
            </div>
          )}
        </div>
      </div>
    ));

    return slides;
  };

  public render() {
    const { slides, currentSlide, layoutType } = this.state;
    const { media, onButtonClick, on3DTourTypeChange } = this.props;

    if (!slides || !slides.length) {
      return null;
    }

    return (
      <div className={style['carousel']}>
        <div className={style['carousel_wrapper']}>
          <div className={style['carousel_fullheight']}>
            <NewbuildingCarousel
              media={mediaSettings}
              firstPaintOptions={firstPaintOptions}
              slides={slides}
              defaultCurrentSlide={currentSlide}
              onSlideChange={this.onSlideChange}
            />
            <ArrowControls
              showLeft={currentSlide > 1}
              showRight={currentSlide < slides.length}
              clickHandler={this.changeSlide}
            />
          </div>
        </div>
        <SlideInfo currentSlide={media[currentSlide - 1]} onButtonClick={onButtonClick} />
        {media[currentSlide - 1]?.tourUrl && (
          <LayoutTypeSwitcher
            initialLayoutType={layoutType}
            onClick={(layoutType: ELayoutTypes) => {
              this.setState({
                slides: this.prepareSlides(media, layoutType),
                layoutType,
              });
              if (layoutType === ELayoutTypes.Tour) {
                on3DTourTypeChange();
              }
            }}
          />
        )}
      </div>
    );
  }

  private onSlideChange = ({ currentSlide }: TOnSlideChangeParams) => {
    this.updateCurrentSlide(currentSlide);
    this.props.selectLayout(currentSlide);
  };

  private changeSlide = (direction: TDirection) => {
    const newCurrentSlide = this.state.currentSlide + direction * 1;
    this.updateCurrentSlide(newCurrentSlide);
  };

  private updateCurrentSlide = (newCurrentSlide: number) => {
    /* в случае отсутствия у нового слайда 3d-тура переключаем еще и тип показываемой планировки */
    if (this.state.layoutType === ELayoutTypes.Tour) {
      const { media } = this.props;
      const layoutType = media[newCurrentSlide - 1]?.tourUrl ? ELayoutTypes.Tour : ELayoutTypes.Layout;

      this.setState({
        slides: this.prepareSlides(media, layoutType),
        currentSlide: newCurrentSlide,
        layoutType,
      });
    } else {
      this.setState({
        currentSlide: newCurrentSlide,
      });
    }
  };
}
